import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import { GatewayPriority } from '@/model/GatewayPriority';
import { toast } from 'react-toastify';
import PaymentGateway from '@/model/PaymentGateway';
import useForm from '@/hooks/useForm';
import { useDialog } from '@/hooks/useDialog';
import { GatewayPrioritySequence } from '@/model/GatewayPrioritySequence';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import { GatewayPriorityContainer } from './ui';
import { FormInputName, ShouldShowModal } from '../types';
import { DeleteContent } from '../components/DeleteContent';

export const GatewayPriorityScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.loading);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.register);
  const [gateways, setGateways] = useState<GatewayPriority[]>([]);
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([]);
  const [sequences, setSequences] = useState<GatewayPrioritySequence[]>([]);

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const confirmDelete = useConfirmDelete();

  const handleOnFetchData = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<GatewayPriority[]>('/gateway-priority/list');
      setGateways(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      paymentType: '',
    },
  });

  const handleOnCancel = (): void => {
    resetForm();
    setSequences([]);
    onToggle();
  };

  const handleOnGetPaymentGateways = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<PaymentGateway[]>('/payment-gateway/find');
      if (data && data.length > 0) {
        setPaymentGateways(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnGet = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      const response = await api.get<PaymentGateway[]>('/payment-gateway/find');
      if (response.data && response.data.length > 0) {
        setPaymentGateways(response.data);
      }
      const { data } = await api.get<GatewayPriority>(`/gateway-priority/get/${id}`);
      if (data) {
        onChangeFormInput(FormInputName.id)(data.id);
        onChangeFormInput(FormInputName.paymentType)(`${data.paymentType}`);
        if (data.gatewayPrioritySequences && data.gatewayPrioritySequences.length > 0) {
          setSequences(data.gatewayPrioritySequences);
        } else {
          const list: GatewayPrioritySequence[] = [];
          list.push({ sequence: 1 } as GatewayPrioritySequence);
          setSequences(list);
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnAddSequence = (): void => {
    let sequence = 1;
    if (sequences && sequences.length > 0) {
      sequences.forEach(data => {
        if (data.sequence >= sequence) {
          sequence = data.sequence + 1;
        }
      });
    }

    const list = [...sequences];
    list.push({ sequence } as GatewayPrioritySequence);
    setSequences(list);
  };

  const handleOnShouldShowModal = async ({
    value,
    newTitleModal,
    id,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    id?: string;
  }): Promise<void> => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    if (id) {
      await handleOnGet(id);
    } else {
      await handleOnGetPaymentGateways();
      handleOnAddSequence();
    }
    onToggle();
  };

  const handleOnChangeGateway = (index: number, paymentGatewayId: string): void => {
    if (paymentGateways && paymentGateways.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < paymentGateways.length; i++) {
        if (paymentGateways[i].id === paymentGatewayId) {
          const updatedSequences = [...sequences];
          updatedSequences[index].paymentGateway = paymentGateways[i];
          setSequences(updatedSequences);
          break;
        }
      }
    }
  };

  const handleOnChangeSequence = (index: number, sequence: number): void => {
    const updatedSequences = [...sequences];
    updatedSequences[index].sequence = sequence;
    setSequences(updatedSequences);
  };

  const handleOnRemoveSequence = (index: number): void => {
    const list: GatewayPrioritySequence[] = [];
    sequences.forEach((data, i) => {
      if (i !== index) {
        list.push(data);
      }
    });
    setSequences(list);
  };

  const handleOnSave = async (): Promise<void> => {
    try {
      setState(States.loading);
      if (isFormValid()) {
        if (sequences.length === 0) {
          toast.error('Adicione ao menos um gateway antes de salvar.');
          return;
        }
        const paylaod = {
          paymentType: formData[FormInputName.paymentType] as unknown as number,
          gatewayPrioritySequences: sequences,
        } as GatewayPriority;
        if (formData[FormInputName.id]) {
          paylaod.id = formData[FormInputName.id] as string;
        }
        await api.post('/gateway-priority', paylaod);
        toast.success('Prioridades de gateway salvas com sucesso!');
        resetForm();
        handleOnCancel();
        handleOnFetchData();
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnClose = (): void => confirmDelete.hide();

  const handleOnRemove = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      await api.delete(`/gateway-priority/${id}`);
      toast.success('Prioridades de gateway removida com sucesso!');
      handleOnClose();
      handleOnFetchData();
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnShowRemove = async (id: string): Promise<void> => {
    confirmDelete.show({
      title: '',
      children: <DeleteContent />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => handleOnClose(),
        },
        {
          title: 'Sim, quero excluir',
          onClick: (): Promise<void> => handleOnRemove(id),
        },
      ],
    });
  };

  useEffect(() => {
    handleOnFetchData();
  }, []);
  return (
    <GatewayPriorityContainer
      state={state}
      title={title}
      visible={visible}
      onCancel={handleOnCancel}
      shouldShowModal={shouldShowModal}
      onShouldShowModal={handleOnShouldShowModal}
      gateways={gateways}
      paymentGateways={paymentGateways}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      sequences={sequences}
      onAddSequence={handleOnAddSequence}
      onChangeGateway={handleOnChangeGateway}
      onChangeSequence={handleOnChangeSequence}
      onRemoveSequence={handleOnRemoveSequence}
      onSave={handleOnSave}
      onRemove={handleOnShowRemove}
    />
  );
};
